import { createFileRoute, Outlet, Link, useNavigate, useLocation } from "@tanstack/react-router"
import { useMemo, useEffect, useState } from "react"
import { z } from "zod"
import { zodValidator } from "@tanstack/zod-adapter"
import { useDateRangeStore } from "~/providers/dateRangeStoreProvider"
import { useSafeLocalStorage } from "~/hooks/useSafeLocalStorage"
import LoadingOverlay from "~/components/reusable/LoadingOverlay"
import { type HeaderTab } from "~/components/reusable/HeaderTabs"
import { VariantProvider } from "~/context/VariantProvider"
import { VariantInformationDateRangePicker } from "~/components/forecast/VariantInformationDateRangePicker"
import { VariantList } from "~/components/product/VariantsList"
import { Module } from "~/components/design-system/pages"
import { ViewByPeriodButton } from "~/components/forecast/ViewByPeriodButton"
import {
  BreadcrumbWrapper,
  useLastNonVariantPath,
  useLastNonVariantSearch,
} from "~/components/reusable/Breadcrumb"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { useFilters } from "~/hooks/useFilters"
import { useRecordRecentlyViewed } from "~/hooks/useRecentlyViewed"
import { filterVariants } from "~/utils/filter"
import { formatVariantId, toStringArray } from "~/utils/helpers"
import { variantTitle } from "~/components/planning/ProductTable/utils/rowLabels"
import { resolveDisplayedVariantIds } from "~/utils/variantScope"
import { first, truncate } from "lodash"

import {
  ChartLine,
  Package,
  CurrencyDollar,
  Sparkle,
  Gauge,
  SquaresFour,
} from "@phosphor-icons/react"
import { Separator } from "~/components/reusable/Separator"
import GlobalWarehouseSelect from "~/components/reusable/GlobalWarehouseSelect"
import GlobalChannelSelect from "~/components/reusable/GlobalChannelSelect"
import { useUser } from "~/providers/userProvider"
import useShowInternalFeatures from "~/hooks/useShowInternalFeatures"
import {
  DialogRoot,
  DialogContentV2,
  DialogHeaderV2,
  DialogBodyV2,
  DialogFooterV2,
  DialogTitle,
} from "~/components/reusable/Dialog"
import { Button } from "~/components/reusable/Button"
import { api } from "~/utils/api"
import {
  HiddenVariantActions,
  ProductUnavailableCard,
} from "~/components/product/ProductUnavailableCard"
import { MissingForecastBanner } from "~/components/product/MissingForecastBanner"
import { useVariants } from "~/hooks/useVariants"

const productSearchSchema = z.object({
  // A single ?variantIds=X parses to a string; coerce to an array so consumers always get string[].
  variantIds: z.preprocess(toStringArray, z.array(z.string()).optional()),
})

export const Route = createFileRoute("/_auth/product/$productId/_layout")({
  component: RouteComponent,
  validateSearch: zodValidator(productSearchSchema),
})

// After a variant is made visible (added to a collection / unhidden) its shouldBeShown flips to
// true; force a full refetch of the variant list so it re-enters the SKU store and the page renders
// (a partial cache refresh won't surface a previously-absent variant). The dialog/card then closes
// itself once the variant reappears.
const invalidateAfterMakeVisible = (utils: ReturnType<typeof api.useUtils>) => {
  void utils.variantTableData.variants.invalidate()
}

function RouteComponent() {
  const { productId } = Route.useParams()
  const { variantIds } = Route.useSearch()
  const { searchStr } = useLocation()

  const { permissions } = useUser()
  const [showInternalFeatures] = useShowInternalFeatures()
  const [monthlyView] = useSafeLocalStorage("monthly-view", true, z.boolean())
  const { weeklyStartDate, weeklyEndDate, monthlyStartDate, monthlyEndDate } = useDateRangeStore(
    (state) => state,
  )
  const startDate = monthlyView ? monthlyStartDate : weeklyStartDate
  const endDate = monthlyView ? monthlyEndDate : weeklyEndDate
  const [lastNonVariantPath] = useLastNonVariantPath()
  const [lastNonVariantSearch] = useLastNonVariantSearch()
  const [currentVariantsSelected, setCurrentVariantsSelected] = useState<
    IVariantSummary[] | undefined
  >(undefined)
  const { filters } = useFilters()
  const [filteredVariants, setFilteredVariants] = useState<IVariantSummary[]>([])
  const navigate = useNavigate({ from: Route.fullPath })

  const rawVariants = useVariants()
  const allVariants = useMemo(
    () => rawVariants?.filter((sku) => sku.productId === productId),
    [rawVariants, productId],
  )
  const allVariantIds = useMemo(() => allVariants.map((v) => v.variantId), [allVariants])

  const productName = first(allVariants)?.productName
  // When a single variant is open, record THAT variant (name + its own route) instead of the
  // whole product. searchStr is router-encoded so the variant selection round-trips on click.
  const singleVariant =
    variantIds?.length === 1 ? allVariants.find((v) => v.variantId === variantIds[0]) : undefined
  useRecordRecentlyViewed(
    singleVariant
      ? {
          type: "variant",
          route: `/product/${productId}${searchStr}`,
          label: singleVariant.variantName ?? singleVariant.productName,
        }
      : productName
        ? { type: "product", route: `/product/${productId}`, label: productName }
        : null,
    // Favorites name the page like its ProductTableV2 row, so same-colour variants stay apart.
    singleVariant
      ? {
          title: variantTitle(singleVariant),
          subtitle: `#${formatVariantId(singleVariant.variantId)}`,
        }
      : { subtitle: `#${productId}` },
  )

  const missingVariantIds = useMemo(() => {
    if (!variantIds || variantIds.length === 0) return []
    return variantIds.filter((id) => !allVariants.some((v) => v.variantId === id))
  }, [variantIds, allVariants])

  const displayedVariantIds = useMemo(
    () => resolveDisplayedVariantIds(allVariantIds, variantIds),
    [allVariantIds, variantIds],
  )

  const displayedVariants = useMemo(() => {
    const displayed = new Set(displayedVariantIds)
    return allVariants.filter((v) => displayed.has(v.variantId))
  }, [allVariants, displayedVariantIds])

  useEffect(() => {
    const filteredVariants = filterVariants(filters, allVariants)
    setFilteredVariants(filteredVariants)
  }, [filters, allVariants])

  useEffect(() => {
    if (!allVariants) return

    if (
      !variantIds ||
      variantIds?.includes("All") ||
      displayedVariants.length === allVariantIds.length
    ) {
      setCurrentVariantsSelected(undefined)
      return
    }

    setCurrentVariantsSelected(displayedVariants.length === 0 ? undefined : displayedVariants)
  }, [variantIds, allVariants, displayedVariants, allVariantIds])

  const showMissingVariantDialog = missingVariantIds.length > 0 && allVariants.length > 0

  const utils = api.useUtils()
  // The product has visible variants but the navigated variantId(s) are hidden. Fetch them so the
  // dialog can offer the same "make it visible" action as the full-page card (not just a dead-end).
  const { data: missingHidden } = api.npi.hiddenVariantsForProduct.useQuery(
    { productId, variantIds: missingVariantIds },
    { enabled: showMissingVariantDialog },
  )
  // Present only when the missing variant(s) actually resolve to hidden ones (else: no longer exist).
  const matchedHidden = missingHidden?.length ? missingHidden : undefined

  const handleDialogClose = () => {
    void navigate({ search: { variantIds: undefined } })
  }

  if (rawVariants.length === 0) {
    return <LoadingOverlay />
  }

  if (allVariants.length === 0) {
    return <DoesNotExist productId={productId} variantIds={variantIds} />
  }

  const tabs: HeaderTab[] = [
    { label: "Overview", path: "/product/$productId", icon: <SquaresFour /> },
    { label: "Demand", path: "/product/$productId/demand", icon: <ChartLine /> },
    { label: "Performance", path: "/product/$productId/performance", icon: <Gauge /> },
    { label: "Inventory", path: "/product/$productId/inventory", icon: <Package /> },
    // {
    //   label: "Suggested orders",
    //   path: "/product/$productId/suggested-orders",
    //   icon: <ShoppingCartSimple />,
    // },
    // {
    //   label: "Collections",
    //   path: "/product/$productId/collections",
    //   icon: <TShirt />,
    // },
    { label: "Finance", path: "/product/$productId/finance", icon: <CurrencyDollar /> },
  ]

  if (permissions.isStaff && showInternalFeatures) {
    tabs.push({
      label: "Forecast analysis",
      path: "/product/$productId/forecast-analysis",
      icon: <Sparkle />,
      hakioOnly: true,
    })
  }

  return (
    <VariantProvider
      variants={displayedVariants}
      productId={productId}
      isMonthly={monthlyView}
      startDate={startDate}
      endDate={endDate}
    >
      <Module
        breadcrumb={
          <div className="flex items-center gap-3">
            <BreadcrumbWrapper
              items={[
                {
                  label: lastNonVariantPath.title ?? "Home",
                  href: lastNonVariantPath.path,
                  search: lastNonVariantSearch,
                },
                {
                  label: truncate(first(allVariants)?.productName, { length: 40 }),
                },
              ]}
            />
            <VariantList
              variantChoices={allVariants.map((v) => ({
                id: v.variantId,
                name: v.variantName ?? v.productName,
                productType: v.productType,
                imageUrl: v.imageUrl,
              }))}
              filteredVariantChoises={filteredVariants.map((v) => ({
                id: v.variantId,
                name: v.variantName ?? v.productName,
                productType: v.productType,
              }))}
              currentVariantsSelected={currentVariantsSelected}
              productId={productId}
            />
          </div>
        }
        actions={
          <>
            <GlobalWarehouseSelect />
            <GlobalChannelSelect />
            <Separator orientation="vertical" className="h-4" />
            <ViewByPeriodButton />
            <VariantInformationDateRangePicker
              variantIds={displayedVariantIds}
              productId={productId}
            />
          </>
        }
        tabs={{ items: tabs, path: "/product/$productId" }}
      >
        <MissingForecastBanner variantIds={displayedVariantIds} />
        <Outlet />
        <DialogRoot
          open={showMissingVariantDialog}
          onOpenChange={(open) => !open && handleDialogClose()}
        >
          <DialogContentV2 closeButton={false}>
            <DialogHeaderV2>
              <DialogTitle>{matchedHidden ? "Variant is hidden" : "Variant not found"}</DialogTitle>
            </DialogHeaderV2>
            <DialogBodyV2>
              <p className="text-fg-subtle paragraph-base">
                The variant{" "}
                <span className="font-medium text-fg-base">{missingVariantIds.join(", ")}</span>{" "}
                {missingVariantIds.length === 1 ? "isn’t" : "aren’t"} shown on this product.
              </p>
              {matchedHidden ? (
                <p className="text-fg-subtle paragraph-base">
                  {missingVariantIds.length === 1 ? "It’s" : "They’re"} hidden. Use the option below
                  to make {missingVariantIds.length === 1 ? "it" : "them"} visible again.
                </p>
              ) : (
                <>
                  <p className="text-fg-subtle paragraph-base">
                    This could be because it was hidden or is no longer being sent to our system.
                  </p>
                  <p className="text-fg-subtle paragraph-base">
                    You can find hidden products under{" "}
                    <Link to="/product-management" className="text-link underline font-medium">
                      Product Management
                    </Link>
                    .
                  </p>
                </>
              )}
            </DialogBodyV2>
            <DialogFooterV2 className="justify-end gap-2">
              <Button variant="neutral" onClick={handleDialogClose}>
                Continue
              </Button>
              {matchedHidden && (
                <HiddenVariantActions
                  variants={matchedHidden}
                  onResolved={() => invalidateAfterMakeVisible(utils)}
                />
              )}
            </DialogFooterV2>
          </DialogContentV2>
        </DialogRoot>
      </Module>
    </VariantProvider>
  )
}

const DoesNotExist = ({ productId, variantIds }: { productId: string; variantIds?: string[] }) => {
  const utils = api.useUtils()
  // A hidden (shouldBeShown = false) variant is filtered out of the SKU store, so the product
  // page can't render it and we land here. Fetch the active-but-hidden variants directly and let
  // the card decide: hidden → informative card + "make it visible" action; none → a specific
  // "not found" message (not a bare 404). Scope to the variant(s) the user navigated to.
  const { data: hiddenVariants, isLoading } = api.npi.hiddenVariantsForProduct.useQuery({
    productId,
    variantIds,
  })

  if (isLoading) {
    return <LoadingOverlay />
  }

  return (
    <ProductUnavailableCard
      productId={productId}
      requestedVariantIds={variantIds}
      hiddenVariants={hiddenVariants ?? []}
      onResolved={() => invalidateAfterMakeVisible(utils)}
    />
  )
}
